/**
 * A show's own timezone, derived from its stored location text.
 *
 * Task: rep badge date/time, 2026-09-17. This app has never converted a
 * city/state into a timezone before: every display anywhere else in it is
 * hardcoded to America/Chicago on purpose (serverTime.js's own docs: "RTF is
 * one team in one city"). A show can genuinely be somewhere else, though, and
 * the owner confirmed the capture date/time under the rep badge should use
 * the SHOW's own timezone, not the rep's.
 *
 * Approach confirmed with the owner rather than guessed: parse the trailing
 * two-letter state abbreviation off `location` (every real show so far is
 * stored as "City, ST", both HITEC 2026 and FS/TEC included) and map it to
 * that state's most common IANA zone. This is right for the large majority
 * of US cities, including both real shows today (San Antonio and Grapevine,
 * both plain Texas, both America/Chicago). It is wrong for a city sitting in
 * the minority zone of a state that spans two: El Paso, TX (Mountain, not
 * Central); the Florida panhandle west of the Apalachicola River (Central,
 * not Eastern); parts of IN, MI, KY, TN, ND, SD, NE, OR, ID. A real per-show
 * timezone field (like users.timezone already exists) would handle those
 * correctly too, but was not built this round: this app runs on two shows a
 * year, both in Texas, and a guessed-but-wrong zone here only ever misdraws
 * a capture time by an hour or two on a display, never anything that
 * actually sends or schedules.
 *
 * Not attempted at all for a personal ("All other times") bucket, which has
 * no location by design: timezoneForContact's own logic falls back to the
 * capturing rep's own stored timezone there instead.
 *
 * Moved here from web/src/lib/showTimezone.js, task: 2026-09-25, so
 * worker/lib/googlesheets.ts (the shared Google Sheet's Captured At / SMS
 * Sent At / Email Sent At columns) can use the exact same logic the app's
 * own UI already uses, rather than a second, hand-duplicated copy of the
 * same STATE_TIMEZONES map that could drift from this one. One file,
 * imported by both the frontend (via the @shared Vite alias, same pattern
 * shared/placeholders.js already uses) and the Worker (a plain relative
 * import; Workers bundle by walking the import graph, unlike Vite's dev
 * server, which needed its own `fs.allow` exception for this directory).
 * web/src/lib/showTimezone.js keeps formatShowDateRange, which needs
 * date-fns, a frontend-only dependency with no reason to exist in the
 * Worker bundle.
 */

/** One representative IANA zone per US state/territory, by population majority. */
const STATE_TIMEZONES = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix', // No DST, unlike the rest of the Mountain zone.
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York', // Panhandle west of the Apalachicola River is Central; majority is Eastern.
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  ID: 'America/Denver', // Northern panhandle is Pacific; Boise and the majority are Mountain.
  IL: 'America/Chicago',
  IN: 'America/New_York', // A handful of northwest/southwest counties are Central.
  IA: 'America/Chicago',
  KS: 'America/Chicago', // A few western counties are Mountain.
  KY: 'America/New_York', // Western Kentucky (Louisville and west) is actually Central.
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/New_York', // Most of the Upper Peninsula is Central.
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago', // Panhandle is Mountain.
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago', // Southwestern counties are Mountain.
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles', // A sliver of the far east is Mountain.
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago', // Western half is Mountain.
  TN: 'America/Chicago', // Eastern Tennessee (Knoxville, Chattanooga) is actually Eastern.
  TX: 'America/Chicago', // El Paso and a few far-west counties are Mountain.
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
  PR: 'America/Puerto_Rico',
};

/** Same fallback this app has always used everywhere, for a location that does not parse. */
export const DEFAULT_TIMEZONE = 'America/Chicago';

/**
 * `location` is free text (Shows.jsx, no format enforced), but every real
 * show so far follows "City, ST". A location that does not match that shape
 * (blank, no state, an unrecognized abbreviation, a future international
 * show) falls back to DEFAULT_TIMEZONE rather than guessing further.
 */
export function timezoneForLocation(location) {
  if (!location) return DEFAULT_TIMEZONE;
  const match = /,\s*([A-Za-z]{2})\s*$/.exec(location.trim());
  if (!match) return DEFAULT_TIMEZONE;
  return STATE_TIMEZONES[match[1].toUpperCase()] ?? DEFAULT_TIMEZONE;
}

/**
 * The timezone to display a contact's own capture date/time in.
 *
 * Confirmed with the owner: the show's own timezone (derived from its
 * location, above) for a contact captured at a real trade show; the
 * capturing rep's own stored timezone (users.timezone, contact.scanned_by_timezone
 * here) for a personal capture, since a personal ("All other times") bucket
 * has no location at all to derive one from. Both fall back to
 * DEFAULT_TIMEZONE if the relevant value is missing.
 */
export function timezoneForContact(contact) {
  if (contact?.show_is_general) return contact?.scanned_by_timezone || DEFAULT_TIMEZONE;
  if (contact?.show_location) return timezoneForLocation(contact.show_location);
  return contact?.scanned_by_timezone || DEFAULT_TIMEZONE;
}
